import { Box, type Theme } from "@mui/material";
import { useTu } from "@src/appV2/i18n/useTu";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { useIsMobile } from "../../AppLayout/useResponsive";
import { BottomSheet } from "../../components/BottomSheet";
import { Dialog } from "../../components/Dialog";
import { DialogFooter } from "../../components/DialogFooter";
import { DialogHeader } from "../../components/DialogHeader";
import { useReportWorkerLeftEarly } from "../../DailyView/api/useReportWorkerLeftEarly";
import { WleFormContent } from "../../Shift/components/WleFormContent";

interface FcmWleDialogProps {
  modalState: UseModalState;
  shiftId: string;
  hasLeftGeofence?: boolean;
  timezone?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * Modal for reporting a Worker Left Early (WLE) after rejecting an FCM (Facility Canceled Me / Sent Home) request.
 * Shown when the user selects "This was a Worker Left Early (WLE)" during FCM rejection.
 */
export function FcmWleDialog(props: FcmWleDialogProps) {
  const { tu } = useTu("chat");
  const { modalState, shiftId, hasLeftGeofence, timezone, onSuccess, onCancel } = props;

  const isMobile = useIsMobile();
  const { mutateAsync: reportWle, isLoading } = useReportWorkerLeftEarly();

  const [description, setDescription] = useState("");
  const [permissionGiven, setPermissionGiven] = useState<boolean | null>(null);

  const handleCancel = () => {
    setDescription("");
    setPermissionGiven(null);
    onCancel();
  };

  const handleSubmit = async () => {
    if (!description.trim() || permissionGiven === null) {
      return;
    }

    try {
      await reportWle({
        shiftId,
        comment: description.trim(),
        leftWithPermission: permissionGiven,
        replacementRequested: false,
      });

      setDescription("");
      setPermissionGiven(null);
      onSuccess();
    } catch {
      showErrorToast(tu("Failed to report Worker Left Early. Please try again."));
    }
  };

  const content = (
    <WleFormContent
      hasLeftGeofence={hasLeftGeofence}
      timezone={timezone}
      reasonDescription={description}
      permissionGiven={permissionGiven}
      onReasonDescriptionChange={setDescription}
      onPermissionGivenChange={setPermissionGiven}
    />
  );

  const footerContent = (
    <DialogFooter
      layout="horizontal"
      primaryAction={{
        label: tu("Submit"),
        onClick: handleSubmit,
        isLoading,
        disabled: !description.trim() || permissionGiven === null,
      }}
      secondaryAction={{
        label: tu("Cancel"),
        onClick: handleCancel,
      }}
    />
  );

  const title = tu("Report Worker Left Early");

  if (isMobile) {
    return (
      <BottomSheet modalState={modalState} dismissible={false}>
        <DialogHeader title={title} onClose={handleCancel} />
        <Box
          sx={(theme: Theme) => ({
            padding: theme.spacing(5),
            overflowY: "auto",
            flex: 1,
          })}
        >
          {content}
        </Box>
        <Box
          sx={(theme: Theme) => ({
            borderTop: `1px solid ${theme.palette.border?.base}`,
          })}
        >
          {footerContent}
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog
      footerContent={footerContent}
      modalState={modalState}
      title={title}
      width={500}
      onClose={handleCancel}
    >
      {content}
    </Dialog>
  );
}
